import dayjs from 'dayjs';

import { getTimeFormat } from './dateDisplay';

/**
 * The grouping period of a grid feed: which bucket a date falls in, and the
 * words that name that bucket.
 *
 * Grid separators used to be days and only days. They are now a period the
 * user picks — clock buckets aligned to the hour, the calendar day, 3-day
 * runs, weeks, months, quarters, years — so a busy day can be read in
 * twenty-minute slices and a long archive in months
 * (docs/plans/card-editor-dates-groups-filters.md, Part 4).
 *
 * Two pure functions do the work, so a group is a run-length scan over the
 * sorted feed and nothing else:
 *
 *   groupKeyFor(value, token)   → 'undated' | a stable bucket key
 *   groupLabelFor(key, token)   → 'TODAY · SEP 13', '3:00 – 3:20 PM · TODAY', …
 *
 * Labels are written upper-case because `.micro-label` renders them that way
 * and the day labels always have been — one convention in the DOM.
 *
 * The tokens are the FROZEN contract with the API's saved-view `group_by`
 * (keepr-api saved filters): `none 15m 20m 30m hour day 3d week month
 * quarter year`.
 */

export const UNDATED_KEY = 'undated';
export const UNDATED_LABEL = 'NO DATE';
export const DEFAULT_GROUP_TOKEN = 'day';

/**
 * Every period, coarsest last — the order the Select offers them in.
 * `minutes` marks the clock buckets (which are also the sub-day tokens:
 * they say nothing useful about a feed sorted by a date without a time).
 */
export const GROUP_TOKENS = [
	{ value: 'none', label: 'None' },
	{ value: '15m', label: '15 minutes', minutes: 15, subDay: true },
	{ value: '20m', label: '20 minutes', minutes: 20, subDay: true },
	{ value: '30m', label: '30 minutes', minutes: 30, subDay: true },
	{ value: 'hour', label: 'Hour', minutes: 60, subDay: true },
	{ value: 'day', label: 'Day' },
	{ value: '3d', label: '3 days' },
	{ value: 'week', label: 'Week' },
	{ value: 'month', label: 'Month' },
	{ value: 'quarter', label: 'Quarter' },
	{ value: 'year', label: 'Year' },
];

const TOKENS_BY_VALUE = GROUP_TOKENS.reduce((acc, token) => {
	acc[token.value] = token;
	return acc;
}, {});

export const isGroupToken = value => Object.prototype.hasOwnProperty.call(TOKENS_BY_VALUE, value);

export const groupTokenMeta = value => TOKENS_BY_VALUE[value] || null;

export const isSubDayToken = value => Boolean(TOKENS_BY_VALUE[value]?.subDay);

/** The 3-day run's width — the only token with an arbitrary anchor. */
const DAYS_PER_RUN = 3;
const MS_PER_DAY = 86400000;

/**
 * A calendar day as an integer, counted from 1970-01-01 in the VIEWER's zone.
 * Reading the local Y/M/D back through `Date.UTC` keeps the arithmetic exact:
 * a DST day is still one day, so a 3-day run never slides by an hour.
 */
const localDayNumber = d => Math.floor(Date.UTC(d.year(), d.month(), d.date()) / MS_PER_DAY);

/** The inverse: local midnight of the day with that number. */
const dayFromNumber = (n) => {
	const utc = new Date(n * MS_PER_DAY);
	return dayjs(new Date(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate()));
};

/** Any stored shape ('YYYY-MM-DD', an ISO instant, a Date) as a dayjs, or null. */
const toDayjs = (value) => {
	if (value === null || value === undefined || value === '') return null;
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed : null;
};

/**
 * Which bucket `value` belongs to under `token`. The key is stable (it is
 * derived from the date alone, never from the item's neighbours), so groups
 * survive an infinite-feed append without re-bucketing, and a collapsed group
 * keeps its identity as more items land in it.
 *
 * `none` has no buckets and returns null; an unreadable or absent date is
 * always 'undated'.
 */
export function groupKeyFor(value, token) {
	if (token === 'none') return null;
	const d = toDayjs(value);
	if (!d) return UNDATED_KEY;

	const meta = TOKENS_BY_VALUE[token];
	if (meta?.minutes) {
		// Aligned to the hour, not to the epoch: a 20-minute bucket always
		// starts at :00, :20 or :40 of a real clock hour.
		const start = d.startOf('hour').add(Math.floor(d.minute() / meta.minutes) * meta.minutes, 'minute');
		return start.format('YYYY-MM-DDTHH:mm');
	}

	switch (token) {
		case '3d': {
			const n = localDayNumber(d);
			return dayFromNumber(Math.floor(n / DAYS_PER_RUN) * DAYS_PER_RUN).format('YYYY-MM-DD');
		}
		case 'week':
			return d.startOf('week').format('YYYY-MM-DD');
		case 'month':
			return d.format('YYYY-MM');
		case 'quarter':
			return `${d.year()}-Q${Math.floor(d.month() / 3) + 1}`;
		case 'year':
			return d.format('YYYY');
		case 'day':
		default:
			return d.format('YYYY-MM-DD');
	}
}

/* ---- Labels ---------------------------------------------------------- */

const KEY_CLOCK = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
const KEY_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const KEY_MONTH = /^(\d{4})-(\d{2})$/;
const KEY_QUARTER = /^(\d{4})-Q([1-4])$/;
const KEY_YEAR = /^(\d{4})$/;

/**
 * A key back to the local moment it names. Built component-wise rather than
 * handed to `dayjs(string)`: 'YYYY-MM' is a UTC month to `Date`, which lands
 * on the previous month west of Greenwich.
 */
const keyToDayjs = (key) => {
	let m = KEY_CLOCK.exec(key);
	if (m) return dayjs(new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]));
	m = KEY_DAY.exec(key);
	if (m) return dayjs(new Date(+m[1], +m[2] - 1, +m[3]));
	m = KEY_MONTH.exec(key);
	if (m) return dayjs(new Date(+m[1], +m[2] - 1, 1));
	m = KEY_QUARTER.exec(key);
	if (m) return dayjs(new Date(+m[1], (+m[2] - 1) * 3, 1));
	m = KEY_YEAR.exec(key);
	if (m) return dayjs(new Date(+m[1], 0, 1));
	return null;
};

const upper = (d, pattern) => d.format(pattern).toUpperCase();

/** "SEP 13", or "SEP 13, 2025" once the year stops being obvious. */
const dateWords = (day, now) => upper(day, day.year() === now.year() ? 'MMM D' : 'MMM D, YYYY');

/** The day a bucket sits on, in the words a reader already knows. */
const dayWords = (day, now) => {
	if (day.isSame(now, 'day')) return 'TODAY';
	if (day.isSame(now.subtract(1, 'day'), 'day')) return 'YESTERDAY';
	return dateWords(day, now);
};

/** "TODAY · SEP 13" — the day separator's label, unchanged since day groups shipped. */
export const dayGroupLabel = (day, now = dayjs()) => {
	const dateLabel = dateWords(day, now);
	if (day.isSame(now, 'day')) return `TODAY · ${dateLabel}`;
	if (day.isSame(now.subtract(1, 'day'), 'day')) return `YESTERDAY · ${dateLabel}`;
	return dateLabel;
};

/** The clock half of a sub-day label, in the viewer's 12h/24h preference. */
const clockLabel = (start, minutes) => {
	const is24h = getTimeFormat() === '24h';
	if (minutes === 60) return is24h ? start.format('HH:00') : upper(start, 'h A');
	const end = start.add(minutes, 'minute');
	if (is24h) return `${start.format('HH:mm')} – ${end.format('HH:mm')}`;
	// One meridiem for the pair unless the bucket straddles noon or midnight.
	const sameHalf = start.format('A') === end.format('A');
	return `${upper(start, sameHalf ? 'h:mm' : 'h:mm A')} – ${upper(end, 'h:mm A')}`;
};

/**
 * What the separator says. `now` is injectable so "TODAY" can be tested (and
 * so a long-lived page reads the same words as the clock it was rendered by).
 */
export function groupLabelFor(key, token, now = dayjs()) {
	if (!key || key === UNDATED_KEY) return UNDATED_LABEL;
	const start = keyToDayjs(key);
	if (!start) return UNDATED_LABEL;

	const meta = TOKENS_BY_VALUE[token];
	if (meta?.minutes) return `${clockLabel(start, meta.minutes)} · ${dayWords(start, now)}`;

	switch (token) {
		case '3d': {
			const end = start.add(DAYS_PER_RUN - 1, 'day');
			// "SEP 11 – 13", "SEP 30 – OCT 2", "DEC 31 – JAN 2, 2027".
			const endPattern = end.isSame(start, 'month') ? 'D' : 'MMM D';
			const endLabel = end.year() === now.year() ? upper(end, endPattern) : `${upper(end, endPattern)}, ${end.year()}`;
			return `${upper(start, 'MMM D')} – ${endLabel}`;
		}
		case 'week':
			return `WEEK OF ${dateWords(start, now)}`;
		case 'month':
			return upper(start, 'MMMM YYYY');
		case 'quarter':
			return `Q${Math.floor(start.month() / 3) + 1} ${start.year()}`;
		case 'year':
			return start.format('YYYY');
		case 'day':
		default:
			return dayGroupLabel(start.startOf('day'), now);
	}
}

/* ---- Which periods a sort can offer ---------------------------------- */

/**
 * Sub-day buckets need a time to bucket BY. A `date` element stores
 * 'YYYY-MM-DD' and reads back as local midnight, so "20 minutes" over it
 * would draw one separator per day with a fake 12:00 AM on it. Created,
 * updated, the primary-date token, and `date-time` elements all carry an
 * instant — they may slice the hour.
 */
export const subDayAllowed = sortMeta => sortMeta?.dataType === 'date-time';

/**
 * Why the grid cannot be cut into periods here, or null when it can (the
 * reason Display ▾ writes under Group). Grouping is the grid's, and it follows
 * the FIRST sort level: "then by" levels (KPR-1637) never make a list date-cut.
 */
export function groupDisabledReasonFor({ viewMode, mapOn, boardOn, isDateSort }) {
	if (viewMode === 'table') return 'The table is one flat list — switch to the grid to group items.';
	if (mapOn) return 'The map shows places, not periods — switch to the grid to group items.';
	if (boardOn) return 'The board groups items by its columns — switch to the grid to group by period.';
	return isDateSort ? null : 'Make the first sort a date to group items.';
}

/** The periods to offer for this sort: everything, or day and coarser. */
export const groupOptionsFor = allowSubDay => (
	allowSubDay ? GROUP_TOKENS : GROUP_TOKENS.filter(token => !token.subDay)
);

/**
 * The period actually in force. An unknown token, or a sub-day token over a
 * date-only sort, falls back to the day — the URL is left alone, so sorting
 * back to a date-time restores what the user asked for.
 */
export const resolveGroupToken = (token, allowSubDay) => {
	if (!isGroupToken(token)) return DEFAULT_GROUP_TOKEN;
	if (!allowSubDay && isSubDayToken(token)) return DEFAULT_GROUP_TOKEN;
	return token;
};

/**
 * The feed, cut into groups: a run-length scan, because the list is already
 * sorted by the very date being grouped. `dateOf` reads whichever date is
 * sorting. Returns null when nothing should be grouped.
 */
export function buildItemGroups(items, token, dateOf, now = dayjs()) {
	if (!token || token === 'none') return null;
	const groups = [];
	let current = null;
	(items || []).forEach((item) => {
		const key = groupKeyFor(dateOf(item), token);
		if (!current || current.key !== key) {
			current = { key, label: groupLabelFor(key, token, now), items: [] };
			groups.push(current);
		}
		current.items.push(item);
	});
	return groups;
}
